import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CustomNumberFormatPipe, ThemeService } from '@wind-log_library/fe-component-library';
import { ReplaySubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import Highcharts from 'highcharts';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-wind-direction-chart',
    templateUrl: './wind-direction-chart.component.html',
    styleUrls: ['./wind-direction-chart.component.scss'],
    standalone: false
})
export class WindDirectionChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fullSize: boolean;
  @Input() isKPIsModule: boolean;
  @Input() radarChartData: any[];

  public chartOptions: Highcharts.Options;
  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;

  public legendData: any;
  public radarChartLabels: string[] = ['0', '15', '30', '45', '60', '75', '90', '105', '120', '135', '150', '165', '180', '195', '210', '225', '240', '255', '270', '285', '300', '315', '330', '345'];
  public isLoading = true;

  private unsubscriber = new ReplaySubject();

  constructor(
    private themeService: ThemeService, 
    private translateService: TranslateService,
    private customNumberFormat: CustomNumberFormatPipe
   ) {
  }

  ngOnInit() {
    this.subscribeToLanguageChange();
    this.isLoading = this.radarChartData?.length > 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.isLoading = this.radarChartData?.length > 0 ? true : false;
    if (changes.radarChartData?.currentValue && changes.radarChartData?.currentValue?.length > 0) {
      this.setChartOptions();
    }
    if (changes.fullSize && this.chartRef) {
      this.chartRef.setSize(
        null,
        this.calculateChartHeight(),
        false
      );
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  private setChartOptions(): void {
    this.legendData = Array.from(new Map(this.radarChartData.map(item => [item.label, { label: item.label, color: item.color }])).values());
    const { xAxis, yAxis, chart, title } = DEFAULT_CHART_OPTIONS;
    const translateService = this.translateService;
    const customNumberFormat = this.customNumberFormat;
    const sectors = [
      '-15–15', '15–45', '45–75', '75–105',
      '105–135', '135–165', '165–195', '195–225',
      '225–255', '255–285', '285–315', '315–345'
    ];
    const series = this.radarChartData.map((item, idx) => ({
      type: 'column',
      name: item.label,
      color: item.color,
      data: item.data,
      pointStart: 0,
      pointInterval: 30,
      borderWidth: 0,
      grouping: false,
      states: { hover: { enabled: false }}
    })).reverse();
    const gridLineColors = ['', '#ffab00', this.themeService.getColor('--red'), this.themeService.getColor('--primary-accent'), this.themeService.getColor('--secondary')];
    this.chartOptions = Highcharts.merge(DEFAULT_CHART_OPTIONS, {
      chart: {
        ...chart,
        polar: true,
        type: 'column',
        height: this.calculateChartHeight(),
        events: {
          render: function () {
            const gridGroup = this.yAxis[0].gridGroup?.element;
            if (!gridGroup) return;
            const gridLines = gridGroup.querySelectorAll('path');
            gridLines.forEach((line, i) => {
              const color = gridLineColors[i] || gridLineColors[gridLineColors.length - 1];
              line.setAttribute('stroke', color);
              line.setAttribute('stroke-width', '1');
              line.setAttribute('stroke-dasharray', '6,4');
            });
          }
        },
        backgroundColor: 'var(--card)'
      },
      legend: { enabled: false },
      title: {
        ...title,
        text: ''
      },
      pane: {
        startAngle: 0,
        endAngle: 360
      },
      xAxis: {
        ...xAxis,
        min: 0,
        max: 360,
        type: 'linear',
        tickInterval: 15,
        reversed: false,
        lineWidth: 0,
        gridLineColor: 'var(--divider)',
        labels: {
          formatter: function () {
            return Number(this.value) % 30 === 0 && this.value !== 0 ? (this.value + '°') : '';
          },
          style: {
            color: 'var(--primary)',
            fontSize: "12px"
          }
        },
        plotLines: [
          {
            value: 0,
            color: 'transparent',
            width: 0,
            zIndex: 5,
            label: {
              text: '0°',
              align: 'center',
              verticalAlign: 'top',
              rotation: 0,
              x: -1,
              y: -15,
              style: { color: 'var(--primary)', fontSize: '12px' }
            }
          }
        ]
      },
      yAxis: {
        ...yAxis,
        min: 0,
        tickAmount: 4,
        labels: {
          formatter: function () {
            return this.value === 0 ? '' : this.value + '%';
          },
          align: 'center',
          y: 3,
          x: 0,
          distance: -10,
          style: {
            color: 'var(--primary)',
            fontSize: "11px"
          }
        },
        gridLineInterpolation: 'circle',
        gridLineColor: 'var(--divider)',
        gridLineDashStyle: 'Dash',
        lineWidth: 0,
        minorGridLineWidth: 0,
        endOnTick: true,
        showLastLabel: true
      },
      tooltip: {
        outside: true,
        useHTML: true,
        shared: false,
        formatter: function() {
          const sector = sectors[this.point.index] || '';
          return `
            <b>${translateService.instant("WIND_DIRECTION_CHART.DURATION")}:</b> ${customNumberFormat.transform(this.point.y)}%<br>
            <b>${translateService.instant("WIND_DIRECTION_CHART.DIRECTION")}:</b> ${sector}<br>
            <b>${translateService.instant("WIND_DIRECTION_CHART.SPEED")}:</b> ${this.series.userOptions.name} m/s
          `;
        }
      },
      plotOptions: {
        series: {
          pointPlacement: 'on',
          borderWidth: 0,
          states: {
            hover: { enabled: false },
            inactive: { enabled: false }
          }
        },
        column: {
          stacking: 'normal',
          pointPlacement: 'on',
          groupPadding: 0,
          pointPadding: 0.05,
          borderWidth: 0
        }
      },
      series: series,
    });
  }

  private subscribeToLanguageChange(): void {
    this.translateService.onLangChange.pipe(
      takeUntil(this.unsubscriber)).subscribe(() => {
        this.setChartOptions();
    });
  }

  private calculateChartHeight(): number {
    if (this.fullSize) {
      return 600;
    }
    return this.isKPIsModule ? 255 : 320;
  }
}
